import { Link } from "react-router-dom"
import { Button } from "./ui/button"
import { useState, useEffect } from "react"
import { formationAPI } from "../services/api" // Adjust the import path as needed

// Type definitions
interface Formation {
  id: number;
  name: string;
  shortDescription: string;
  image?: string;
  categoryName?: string;
  totalDuration?: number;
  chapitreCount?: number;
  gratuit: boolean;
  price?: number;
  currency?: string;
  formateur?: {
    id: number;
    name: string | null;
    photo: string | null;
  } | null;
}

interface Course {
  id: number;
  title: string;
  description: string;
  image: string;
  category: string;
  instructor: string;
  instructorImage: string;
  duration: string;
  lessons: number;
  price: string;
}

export default function HomePage() {
  const [featuredCourses, setFeaturedCourses] = useState<Course[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchFeaturedCourses = async () => {
      try {
        setLoading(true)
        const response = await formationAPI.getLastThree()

        // Helper function to convert minutes to hours and minutes format
        const formatDuration = (totalMinutes: number): string => {
          if (totalMinutes === 0) return "0 min"

          const hours = Math.floor(totalMinutes / 60)
          const minutes = totalMinutes % 60

          if (hours === 0) {
            return `${minutes} min`
          } else if (minutes === 0) {
            return hours === 1 ? `${hours} heure` : `${hours} heures`
          } else {
            const hourText = hours === 1 ? "heure" : "heures"
            return `${hours} ${hourText} ${minutes} min`
          }
        }

        // Transform the API data to match the component's expected format
        const transformedCourses: Course[] = response.data.map((formation: Formation, index: number) => ({
          id: formation.id,
          title: formation.name,
          description: formation.shortDescription,
          image: formation.image || "/placeholder.svg",
          category: formation.categoryName || "Cours magistraux",
          instructor: formation.formateur?.name || "Elille academy",
          instructorImage: formation.formateur?.photo || "/avatar.png?height=100&width=100",
          duration: formatDuration(formation.totalDuration || 0),
          lessons: formation.chapitreCount || 0,
          price: formation.gratuit === true ? "Gratuit" : `${formation.price} ${formation.currency || 'MAD'}`,
        }))

        setFeaturedCourses(transformedCourses)
      } catch (err: any) {
        console.error('Error fetching featured courses:', err)

        // More detailed error handling
        if (err.response?.status === 404) {
          setError('API endpoint not found. Please check backend server.')
        } else if (err.code === 'ECONNREFUSED') {
          setError('Cannot connect to server. Please check if backend is running.')
        } else {
          setError(`Error loading formations: ${err.message}`)
        }

        // Fallback to empty array or default data if needed
        setFeaturedCourses([])
      } finally {
        setLoading(false)
      }
    }

    fetchFeaturedCourses()
  }, [])

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="w-full py-8 md:py-16 lg:py-24 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800">
        <div className="container px-4 md:px-6">
          <div className="grid gap-6 lg:grid-cols-2 lg:gap-12 items-center">
            <div className="flex flex-col justify-center space-y-4">
              <div className="space-y-2">
                <h1 className="text-3xl font-serif font-bold tracking-tighter sm:text-5xl xl:text-6xl/none">
                  Éduquer pour un Maroc juste, égalitaire et solidaire.
                </h1>
                <p className="max-w-[600px] text-gray-500 md:text-xl dark:text-gray-400">
                  Elille Academy propose des ressources éducatives engagées pour sensibiliser, informer et former sur l’inclusion, la diversité et l’égalité de genre.
                </p>
              </div>
              <div className="flex flex-col gap-2 min-[400px]:flex-row">
                <Link to="/courses" className="px-8 py-2 bg-[#00088E] hover:bg-blue-700 text-white rounded-[3px] text-center">
                  Découvrir nos formations
                </Link>
              </div>
            </div>
            <div className="mx-auto lg:ml-auto">
              <img
                src="/Home.png?height=550&width=550"
                alt="Elille Academy - Éducation aux droits humains"
                width={550}
                height={550}
                className="rounded-xl object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Courses - Only show section if courses exist */}
      {(!loading && !error && featuredCourses.length > 0) && (
        <section className="w-full py-12 md:py-24 lg:py-32">
          <div className="container px-4 md:px-6">
            <div className="flex flex-col items-center justify-center space-y-4 text-center">
              <div className="space-y-2">
                <h2 className="text-3xl font-serif font-bold tracking-tighter md:text-4xl">Nos formations populaires</h2>
                <p className="max-w-[900px] text-gray-500 md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed dark:text-gray-400">
                  Elille Academy propose des ressources éducatives sur l'inclusion, la diversité et l'égalité de genre et la justice sociale pour sensibiliser, informer et former.
                </p>
              </div>
            </div>

            {/* Courses Grid */}
            <div className="grid grid-cols-1 gap-6 mt-8 md:grid-cols-2 lg:grid-cols-3">
              {featuredCourses.map((course, index) => {
                // Alternating colors for categories
                const categoryColors = [
                  "bg-blue-100 text-[#00088E]",
                  "bg-red-100 text-red-600",
                  "bg-green-100 text-green-600",
                ]
                const colorClass = categoryColors[index % 3]

                return (
                  <Link to={`/courses/${course.id}`} key={course.id} className="transition-transform hover:scale-105">
                    <div className="border rounded-lg shadow-sm overflow-hidden">
                      <div className="p-0">
                        <div className="relative h-48 w-full">
                          <img
                            src={course.image}
                            alt={course.title}
                            className="w-full h-full object-cover rounded-t-lg"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = "/placeholder.svg"
                            }}
                          />
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}
                          >
                            {course.category}
                          </span>
                        </div>
                        <h3 className="text-xl mb-2 font-bold">{course.title}</h3>
                        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-2">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                          </svg>
                          <span>{course.duration}</span>
                          <svg className="w-4 h-4 ml-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                          </svg>
                          <span>{course.lessons} modules</span>
                        </div>
                        <div className="flex items-center">
                          <img
                            src={course.instructorImage}
                            alt={course.instructor}
                            width={24}
                            height={24}
                            className="rounded-full mr-2"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = "/placeholder.svg"
                            }}
                          />
                          <span className="text-sm">{course.instructor}</span>
                        </div>
                      </div>
                      <div className="flex justify-between items-center p-4 pt-0 border-t">
                        <span className="text-lg font-bold">{course.price}</span>
                        <button className="text-[#00088E] hover:text-blue-700 hover:bg-blue-50 px-2 py-1 rounded text-sm">
                          Voir la formation
                          <svg className="ml-1 h-4 w-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </Link>
                )
              })}
            </div>

            {/* Show "View all formations" button only if there are courses */}
            <div className="flex justify-center mt-8">
              <Link to="/courses" className="px-4 py-2 border border-[#00088E] text-[#00088E] hover:bg-blue-50 rounded-md">
                Voir toutes les formations
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Our Mission */}
      <section className="w-full py-12 md:py-24 lg:py-32 bg-gray-100 dark:bg-gray-900">
        <div className="container px-4 md:px-6">
          <div className="grid gap-6 lg:grid-cols-2 lg:gap-12 items-center">
            <div>
              <img
                src="/mission.png?height=400&width=600"
                alt="Notre mission"
                width={600}
                height={400}
                className="rounded-xl object-cover"
              />
            </div>
            <div className="flex flex-col justify-center space-y-4">
              <div className="space-y-2">
                <h2 className="text-3xl font-serif font-bold tracking-tighter md:text-4xl">Notre mission</h2>
                <h3 className="text-3xl font-serif font-bold tracking-tighter md:text-4xl">Apprendre. Inspirer. Transformer.</h3>
                <p className="text-gray-500 dark:text-gray-400 text-lg">
                  Elille Academy est une initiative engagée pour une éducation à l’inclusion, à la diversité et à l’égalité de genre. Elle privilégie une approche participative et interactive, centrée sur l’apprentissage collectif et accessible à toutes et tous.
                </p>
                <p className="text-gray-500 dark:text-gray-400 text-lg mt-4">
                  Notre ambition est de rendre les outils pédagogiques disponibles à un large public, afin de sensibiliser, informer et former sur ces enjeux essentiels.
                </p>
                <p className="text-gray-500 dark:text-gray-400 text-lg mt-4">
                  Nous croyons profondément que l’éducation est un levier puissant pour bâtir une société plus juste, plus égalitaire et plus solidaire. À travers nos formations, nos ressources et nos événements, nous cultivons une communauté engagée et mobilisée autour des valeurs de diversité, d’égalité et d’inclusion.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Join Community */}
      <section className="w-full py-12 md:py-24 lg:py-32 bg-[#EFF5FF] dark:bg-gray-900">
        <div className="container px-4 md:px-6 text-center">
          <div className="mx-auto max-w-3xl space-y-4">
            <h2 className="text-3xl font-serif font-bold tracking-tighter md:text-4xl">Rejoignez notre communauté</h2>
            <p className="text-gray-500 dark:text-gray-400 text-lg">
            Suivez-nous sur les réseaux sociaux pour rester informé.es de nos formations.
            </p>
            <div className="flex justify-center space-x-4 mt-6">
            <a
              href="https://www.facebook.com/elillecollective.maroc/?locale=fr_FR"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="outline" size="icon" className="rounded-full">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-5 w-5"
                  aria-hidden
                >
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                </svg>
                <span className="sr-only">Facebook</span>
              </Button>
            </a>
            <a
              href="https://www.linkedin.com/company/elille-project/about/?viewAsMember=true"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="outline" size="icon" className="rounded-full">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-5 w-5"
                  aria-hidden
                >
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                  <rect width="4" height="12" x="2" y="9"></rect>
                  <circle cx="4" cy="4" r="2"></circle>
                </svg>
                <span className="sr-only">LinkedIn</span>
              </Button>
            </a>
            <a href="mailto:contact@elilleacademy.com">
              <Button variant="outline" size="icon" className="rounded-full">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  className="h-5 w-5"
                  aria-hidden
                >
                  <path
                    fill="currentColor"
                    d="M24 5.457v13.086c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 18.543V5.457c0-2.023 2.309-3.178 3.927-1.964L12 10.109l8.073-6.616C21.69 2.279 24 3.434 24 5.457z"
                  />
                </svg>
                <span className="sr-only">Gmail — contact@elilleacademy.com</span>
              </Button>
            </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
